import React, { useState } from 'react'
import './ResumeTemplateModal.scss'

interface ResumeTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectTemplate: (templateId: string) => void
}

interface Template {
  id: string
  name: string
  description: string
  previewImage: string
}

// Template definitions
const TEMPLATES: Template[] = [
  {
    id: 'default',
    name: 'Classic',
    description: 'A traditional, professional resume format that works well for most industries. Clean and easy to read, perfect for ATS systems.',
    previewImage: '/images/templates/default.png'
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'A contemporary design with improved visual hierarchy. Great for creative and tech roles while maintaining professionalism.',
    previewImage: '/images/templates/modern.png'
  },
  {
    id: 'creative',
    name: 'Creative',
    description: 'A bold, eye-catching design that showcases your personality. Ideal for design, marketing, and creative industry positions.',
    previewImage: '/images/templates/creative.png'
  }
]

function ResumeTemplateModal({ isOpen, onClose, onSelectTemplate }: ResumeTemplateModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)

  if (!isOpen) return null

  const handleSelect = (templateId: string) => {
    setSelectedTemplate(templateId)
  }

  const handleConfirm = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate)
      setSelectedTemplate(null)
    }
  }

  const handleClose = () => {
    setSelectedTemplate(null)
    onClose()
  }

  return (
    <div className="modal-overlay">
      <div className="resume-template-modal-content">
        <div className="resume-template-modal-header">
          <h3 className="resume-template-modal-title">Choose Resume Template</h3>
          <button
            className="resume-template-modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="resume-template-modal-body">
          <p className="resume-template-modal-description">
            Select a template that best matches your style and the job you're applying for.
          </p>
          
          <div className="template-grid">
            {TEMPLATES.map((template) => (
              <div
                key={template.id}
                className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                onClick={() => handleSelect(template.id)}
              >
                <div className="template-preview">
                  <img 
                    src={template.previewImage} 
                    alt={`${template.name} template preview`}
                    className="template-preview-image"
                  />
                  {selectedTemplate === template.id && (
                    <div className="template-selected-badge">
                      <span className="template-selected-icon">✓</span>
                    </div>
                  )}
                </div>
                <div className="template-info">
                  <h4 className="template-name">{template.name}</h4>
                  <p className="template-description">{template.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="resume-template-modal-footer">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={!selectedTemplate}
          >
            Generate Resume
          </button>
        </div>
      </div>
    </div>
  )
}

export default ResumeTemplateModal

